/**
 * Options for password generation.
 */
export interface PasswordOptions {
  /** The max length of the generated password. */
  maxLength: number;
  /** The min length of the generated password. */
  minLength: number;
  /** Minimum number of uppercase characters. */
  minUppercase: number;
  /** Minimum number of lowercase characters. */
  minLowercase: number;
  /**  Minimum number of numeric characters. */
  minNumbers: number;
  /** Minimum number of special characters. */
  minSpecials: number;
}

/**
 * Generate a random password based on specified requirements.
 *
 * @param {PasswordOptions} options - Options for password generation.
 * @returns {string} - The generated password.
 * @throws {Error} - If the specified requirements are invalid.
 *
 * @example
 * const passwordOptions = {
 *   maxLength: 255,
 *   minLength: 8,
 *   minUppercase: 2,
 *   minLowercase: 2,
 *   minNumbers: 2,
 *   minSpecials: 1,
 * };
 * const generatedPassword = generatePassword(passwordOptions);
 */
export const generatePassword = (options?: PasswordOptions): string => {
  const { minLength, maxLength, minUppercase, minLowercase, minNumbers, minSpecials } = options ?? defaultOptions;
  const passwordLength = Math.max(minLength, Math.min(defaultLength, maxLength));

  const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
  const numberChars = '0123456789';
  const specialChars = '!@#$%^&*()-=_+[]{}|;:,.<>?';

  const allChars = uppercaseChars + lowercaseChars + numberChars + specialChars;

  if (minUppercase + minLowercase + minNumbers + minSpecials > passwordLength) {
    throw new Error('Invalid password requirements');
  }

  /**
   * Get a random character from a string.
   *
   * @param {string} str - The input string.
   * @returns {string} - A randomly selected character from the input string.
   * @private
   */
  const getRandomChar = (str: string): string => str[Math.floor(Math.random() * str.length)] ?? '';

  /**
   * Get a random subset of characters from a string.
   *
   * @param {string} str - The input string.
   * @param {number} count - The number of characters to include in the subset.
   * @returns {string} - A string containing the randomly selected subset of characters.
   * @private
   */
  const getRandomSubset = (str: string, count: number): string =>
    Array.from({ length: count }, () => getRandomChar(str)).join('');

  /**
   * Shuffle characters in a string using Fisher-Yates shuffle algorithm.
   *
   * @param {string} str - The input string.
   * @returns {string} - A shuffled version of the input string.
   * @private
   */
  const shuffleString = (str: string): string => {
    const array = Array.from(str);
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j] as unknown as string, array[i] as unknown as string];
    }
    return array.join('');
  };

  const password =
    getRandomSubset(uppercaseChars, minUppercase) +
    getRandomSubset(lowercaseChars, minLowercase) +
    getRandomSubset(numberChars, minNumbers) +
    getRandomSubset(specialChars, minSpecials) +
    Array.from({ length: passwordLength - minUppercase - minLowercase - minNumbers - minSpecials })
      .map(() => getRandomChar(allChars))
      .join('');

  const shuffledPassword = shuffleString(password);

  return shuffledPassword;
};

const defaultLength = 16;

export const defaultOptions: PasswordOptions = {
  maxLength: 255,
  minLength: 8,
  minNumbers: 2,
  minSpecials: 1,
  minLowercase: 2,
  minUppercase: 2,
};
